import { Params } from '@angular/router';
import SHA from 'sha.js';

import { Os, Type } from '../models/events/basic-event.model';
import { ZenitEventName } from '../models/zenit-general.model';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { CookieGroup } from '@core/domain/models/user-consent.models';
import { Resolutions } from '@core/domain/state/core.state';
import { userConsentPoliciesValues } from '@core/services/user-consent/user-consent.service';
import { getCookie } from '@core/utils';

/** Currently implemented version of each Zenit event */
export const getEventVersion = (eventName: ZenitEventName) => {
    switch (eventName) {
        case ZenitEventName.PageHit:
            return '1.1.0';
        case ZenitEventName.AddToCart:
            return '1.1.0';
        case ZenitEventName.PurchaseConfirmed:
            return '1.1.0';
        case ZenitEventName.ImpressionsHit:
            return '1.1.1';
        case ZenitEventName.SearchAssistant:
            return '1.0.0';
    }
};

/**
 * Saved values for each consent category
 * IMPORTANT: ID's (C000x) might be different according to your implementation
 * Standard ID's for OneTrust are the following:
    StrictlyNecessary = 'C0001'
    Analytics = 'C0002'
    FeaturesOrCustomization = 'C0003'
    Advertising = 'C0004'
    SocialMedia = 'C0005'
 *
 * @param cookieGroupMap
 * @returns string
 */
export const getConsentCategories = (cookieGroupMap: Record<userConsentPoliciesValues, CookieGroup>): string => {
    let consentCategories: string = '';
    for (const [key, value] of Object.entries(cookieGroupMap)) {
        consentCategories += `${key}:${value.allowed ? '1' : '0'}`;
        if (Object.keys(cookieGroupMap).indexOf(key) !== Object.keys(cookieGroupMap).length - 1) {
            consentCategories += ',';
        }
    }

    return consentCategories || null;
};

export const getConsentedDate = (): string => {
    const cookieValue = getCookie(document.cookie, 'OptanonConsent');
    const propertyName = 'datestamp';

    if (cookieValue) {
        const properties = new URLSearchParams(cookieValue);
        const cookiePropertyValue = properties.get(propertyName);

        if (cookiePropertyValue) {
            const regex: RegExp = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{2}) (\d{4}) (\d{2}:\d{2}:\d{2}) (\w{3}) (\d{4}) \(.+\)/;
            const match: RegExpExecArray = regex.exec(cookiePropertyValue);

            if (match) {
                const [, dayOfWeek, month, day, year, time, , timezone] = match;

                return `${dayOfWeek}, ${day} ${month} ${year} ${time}+${timezone}`;
            }
        }
    }

    return null;
};

/**
 * Gets specific utm param from queryParams
 *
 * @param queryParams
 * @param param
 * @returns string
 */
export const getUtmParam = (queryParams: Params, param: string): string => {
    const _param = /(gclid)/.exec(param) ? param : `utm_${param}`;
    return queryParams?.[_param] || null;
};

/**
 *
 * @param language
 * @returns string
 */
export const getLanguage = (language: string): string => {
    return language.replace('-', '_');
};

/**
 * Gets the client device type from the resolution (mobile|tablet|desktop)
 *
 * @param resolution
 * @returns Type
 */
export const getType = (resolution: Resolutions): Type => {
    let device: Type;

    if (resolution === Resolutions.MOBILE) {
        device = Type.mobile;
    } else if (resolution === Resolutions.TABLET) {
        device = Type.tablet;
    } else {
        device = Type.desktop;
    }

    return device;
};

/**
 * Gets the OS type from the userAgent string
 *
 * @param userAgent
 * @returns Os
 */
export const getOs = (userAgent: string): Os => {
    if (/(Android)\s(\S*)/.exec(userAgent)) return Os.android;
    if (/(iPhone|iPod|iPod touch);/.exec(userAgent)) return Os.ios;
    if (/(iPad;)/.exec(userAgent)) return Os.ipados;
    if (/(Macintosh;)/.exec(userAgent)) return Os.mac;
    if (/(Win64|Win32|Windows|Windows Phone\s(\S*));/.exec(userAgent)) return Os.windows;
    if (/(WebOS;)/.exec(userAgent)) return Os.android;
    if (/(Linux)\s(\S*)/.exec(userAgent)) return Os.linux;
    return null;
};

/**
 * Gets the client device OS version, examples: 15.4.1, 9
 *
 * @param userAgent
 * @returns string
 */
export const getOsVersion = (userAgent: string): string => {
    const match: string[] = /(Android|iPhone|iPad|iPod|webOS|BlackBerry|Windows Phone|Windows|Mac OS X|CPU OS|Linux)\s\w*\s*\d(\w|\.)+/.exec(userAgent);
    if (match) {
        const version = match[0].split(' ');
        return version[version.length - 1];
    }
    return null;
};

export const getUserType = (userType: string): string => {
    return userType === 'R' ? 'registered' : 'guest';
};

export const getUserIdToken = (userId: number) => {
    if (userId && userId !== -1002) {
        return SHA('sha256').update(userId.toString()).digest('hex');
    }
    return null;
};

export const getReferrer = (_document: Document, window: CommonWindow, referredSent: boolean) => {
    if (!!_document.referrer && new URL(_document.referrer).host !== window.location.host && !referredSent) {
        return {
            url: _document.referrer,
        };
    }

    return null;
};
